import { DatePipe } from '@angular/common';
import { Component, computed, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ChartData } from 'chart.js';
import { DateTime } from 'luxon';
import { ChartModule } from 'primeng/chart';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { PageComponent } from '../../util/page/page';
import {
  ConsumptionService,
  ConsumptionUnitOfMeasure,
  PortalConsumptionEntry,
  PortalConsumptionUnit,
} from '../models/portal.models';
import { PortalService } from '../services/portal.service';

interface ConsumptionChart {
  service: ConsumptionService;
  label: string;
  unit: string;
  data: ChartData<'bar'>;
}

interface UnitOption {
  label: string;
  value: string;
}

const SERVICE_LABELS: Record<ConsumptionService, string> = {
  HEATING: 'Heizung',
  HOT_WATER: 'Warmwasser',
  COLD_WATER: 'Kaltwasser',
  COOLING: 'Kühlung',
};

const UNIT_LABELS: Record<ConsumptionUnitOfMeasure, string> = {
  KWH: 'kWh',
  HCU: 'Einheiten',
  M3: 'm³',
};

const SERVICE_COLORS: Record<ConsumptionService, string> = {
  HEATING: 'rgba(220, 90, 70, 0.85)',
  HOT_WATER: 'rgba(233, 150, 60, 0.85)',
  COLD_WATER: 'rgba(66, 86, 187, 0.85)',
  COOLING: 'rgba(60, 170, 170, 0.85)',
};

@Component({
  selector: 'app-home-page',
  imports: [PageComponent, TableModule, ChartModule, SelectModule, FormsModule, DatePipe],
  templateUrl: './home-page.html',
  styleUrl: './home-page.css',
})
export class HomePage implements OnInit {
  readonly loadError = signal(false);
  readonly selectedUnitId = signal<string | null>(null);

  readonly chartOptions = {
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, ticks: { precision: 0 } },
    },
  };

  /** Einheiten mit Verbrauchsdaten des Kontakts. */
  readonly consumptionUnits = computed(() => this.portal.consumption()?.units ?? []);

  /** Auswahloptionen für den Einheiten-Umschalter (nur bei mehreren Einheiten sichtbar). */
  readonly unitOptions = computed<UnitOption[]>(() =>
    this.consumptionUnits().map((unit) => ({
      label: this.formatUnitLabel(unit),
      value: unit.unitId,
    })),
  );

  /** Ausgewählte Einheit, mit Fallback auf die erste, solange nichts gewählt wurde. */
  readonly selectedUnit = computed<PortalConsumptionUnit | null>(() => {
    const units = this.consumptionUnits();
    return units.find((unit) => unit.unitId === this.selectedUnitId()) ?? units[0] ?? null;
  });

  readonly consumptionCharts = computed<ConsumptionChart[]>(() => {
    const unit = this.selectedUnit();
    return unit ? this.buildCharts(unit.entries) : [];
  });

  constructor(public readonly portal: PortalService) {}

  ngOnInit(): void {
    this.portal.loadMe().subscribe({ error: () => this.loadError.set(true) });
    this.portal.loadConsumption().subscribe({ error: () => this.loadError.set(true) });
  }

  /** Dropdown-Label analog zur Einheiten-Tabelle: Adresse (Fallback Liegenschaftsname) + Einheit. */
  private formatUnitLabel(unit: PortalConsumptionUnit): string {
    const suffix = unit.unitLabel ? ` (${unit.unitLabel})` : '';
    return `${unit.address ?? unit.estateName} — ${unit.unitDisplayId}${suffix}`;
  }

  /** Baut je Service (Heizung/Warmwasser/…) ein monatliches Balkendiagramm. */
  private buildCharts(entries: PortalConsumptionEntry[]): ConsumptionChart[] {
    const byService = new Map<ConsumptionService, PortalConsumptionEntry[]>();
    for (const entry of entries) {
      const list = byService.get(entry.service) ?? [];
      list.push(entry);
      byService.set(entry.service, list);
    }

    const charts: ConsumptionChart[] = [];
    for (const [service, serviceEntries] of byService) {
      const periods = [...new Set(serviceEntries.map((e) => e.period))].sort();
      const totals = new Map<string, number>();
      for (const entry of serviceEntries) {
        totals.set(entry.period, (totals.get(entry.period) ?? 0) + entry.amount);
      }

      charts.push({
        service,
        label: SERVICE_LABELS[service],
        unit: UNIT_LABELS[serviceEntries[0].unitofmeasure],
        data: {
          labels: periods.map((p) => this.formatPeriod(p)),
          datasets: [
            {
              label: SERVICE_LABELS[service],
              data: periods.map((p) => Math.round((totals.get(p) ?? 0) * 100) / 100),
              backgroundColor: SERVICE_COLORS[service],
              borderRadius: 4,
            },
          ],
        },
      });
    }
    return charts;
  }

  private formatPeriod(period: string): string {
    return DateTime.fromFormat(period, 'yyyy-MM').setLocale('de').toFormat("LLL ''yy");
  }
}
